import React, { useState, useEffect } from 'react';
import { CheckIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { CONFIG, authHeaders, authFetch } from '../utils/config';
import { QRCodeService } from '../utils/qrCodeService';
import { VCardService }  from '../services/vcardService';
import VCardPicker from './VcardPicker';
import VCardEditModal from './VCardEdit';
import { Link } from 'react-router-dom';
import { useContent } from '../hooks/useContent';

const imageCache = new Map();

const isVCardUrl = (url) =>
  !!url && url.startsWith(`${CONFIG.BASE_URL}/vcard/`);

const getVCardUuid = (url) => {
  if (!isVCardUrl(url)) return null;

  const uuid = url
    .replace(`${CONFIG.BASE_URL}/vcard/`, '')
    .replace(/\/$/, ''); 
  return uuid || null;
};

const formatRedirectUrl = (url) => {
  if (!url) return '';

 
  if (isVCardUrl(url)) {
    return `VCard | ${url}`;
  }

  return url;
};

const QRCodeEditorInline = ({ qrCode, onUpdate, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({});
  const [highQualityImage, setHighQualityImage] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const { isDark } = useTheme();
  const { content } = useContent('qrCodeEditorInline');

  const [focused, setFocused] = useState(false);

  const [vcard, setVCard] = useState(null);
  const [vcardNumber, setVCardNumber] = useState(null);
  const [vcardFetchError, setVCardFetchError] = useState(false);

  useEffect(() => {

    const uuid = getVCardUuid(formData.redirect_url);
    if (!uuid) {
      setVCard(null);
      setVCardFetchError(false);
      return;
    }

    setVCardFetchError(false);

    VCardService.getPublic(uuid)
      .then((data) => {
        setVCard(data.data);
	setVCardNumber(data.number)
        setVCardFetchError(false);
      })
      .catch(() => {
        setVCard(null);
	setVCardNumber(null)
        setVCardFetchError(true);
      });
  }, [formData.redirect_url]);

  const isRedirectUrlVCard = Boolean(vcard);

  /* on lit les champs uniquement si on a une VCard */
  const { name, phone, mail, company } = vcard?.data ?? {};
 
  const qrImage = highQualityImage || QRCodeService.getQRThumbnailUrl(qrCode);

  useEffect(() => {
    if (qrCode) {
      switch (qrCode.item_type) {
        case 'url':
        case 'classic':
          setFormData({
            redirect_url: qrCode.redirect_url || qrCode.url || '',
            max_redirect: qrCode.qr_code_max_use ?? '',
            validity_time_min: ''
          });
          break;
        case 'ai':
          setFormData({
            redirect_url: qrCode.redirect_url || qrCode.url || '',
            max_redirect: qrCode.qr_code_max_use ?? '',
            validity_time_min: ''
          });
          break;
        case 'gs1':
          setFormData({
            base_url: qrCode.base_url || 'https://id.gs1.org',
            gtin: qrCode.gtin || '',
            lot: qrCode.lot || '',
            exp: qrCode.exp || '',
            lang: qrCode.lang || 'fr',
            link_type: qrCode.link_type || 'pip',
            max_redirect: qrCode.qr_code_max_use ?? '',
            validity_time_min: ''
          });
          break;
        default:
          setFormData({
            redirect_url: qrCode.redirect_url || qrCode.url || '',
            max_redirect: qrCode.qr_code_max_use ?? '',
            validity_time_min: ''
          });
      }
    }
  }, [qrCode]);
  useEffect(() => {
    const loadHighQualityImage = async () => {
      if (!qrCode?.uuid) return;
      
      setIsLoadingImage(true);
      
      try {
        const cacheKey = `hq_${qrCode.uuid}`;
        if (imageCache.has(cacheKey)) {
          setHighQualityImage(imageCache.get(cacheKey));
          setIsLoadingImage(false);
          return;
        }
        
        const params = new URLSearchParams({
          miniature: 'true',
          miniature_quality: '90',
          miniature_size: '256'
        });

        const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${qrCode.uuid}?${params.toString()}`, {
          method: 'GET',
          headers: authHeaders()
        });

        if (response.ok) {
          const data = await response.json();
          const highQualityUrl = QRCodeService.getQRThumbnailUrl(data);
          
          imageCache.set(cacheKey, highQualityUrl);
          setHighQualityImage(highQualityUrl);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'image haute qualité:', error);
      } finally {
        setIsLoadingImage(false);
      }
    };

    loadHighQualityImage();
  }, [qrCode?.uuid]); 

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      let redirectUrl = formData.redirect_url;

      if (qrCode.item_type === 'gs1') {
        const params = new URLSearchParams();
        if (formData.base_url) params.append('base_url', formData.base_url);
        if (formData.gtin) params.append('gtin', formData.gtin);
        if (formData.lot) params.append('lot', formData.lot);
        if (formData.exp) params.append('exp', formData.exp);
        if (formData.lang) params.append('lang', formData.lang);
        if (formData.link_type) params.append('link_type', formData.link_type);

        const gs1Response = await authFetch(`${CONFIG.BASE_URL}/qr-code/gs1/generate?${params.toString()}`, {
          method: 'GET',
          headers: authHeaders({
            'Content-Type': 'application/json'
          })
        });

        if (!gs1Response.ok) {
          throw new Error('Erreur lors de la génération du lien GS1');
        }

        const gs1Data = await gs1Response.json();
        redirectUrl = gs1Data.link;
      }

      const payload = {
        redirect_url: redirectUrl
      };

      if (formData.max_redirect === '' || formData.max_redirect === null || formData.max_redirect === undefined) {
        payload.max_redirect = null;
      } else {
        payload.max_redirect = parseInt(formData.max_redirect);
      }


      if (formData.validity_time_min && formData.validity_time_min !== '') {
        const localDate = new Date(formData.validity_time_min);
        payload.valid_until = localDate.toISOString().slice(0, 19) + '+00:00';
      }

      const miniatureParams = new URLSearchParams({
        miniature: 'true',
        miniature_quality: '70',
        miniature_size: '64'
      });
      
      const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${qrCode.uuid}?${miniatureParams.toString()}`, {
        method: 'PATCH',
        headers: authHeaders({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload)
      });
      console.log(response)

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const updatedQRCode = await response.json();
      setSuccess('QR Code mis à jour avec succès !');
      onUpdate?.(updatedQRCode);
      
      setTimeout(() => {
        setSuccess('');
        onCancel?.();
      }, 2000);

    } catch (err) {
      setError(err.message || content.saveError);
      console.error('Erreur mise à jour:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const renderClassicQRFields = () => (
    <div className="space-y-4">
<div>
          <label className={`block text-sm font-medium mb-2 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
	  {content.redirectLabel}
          </label>
	    <div className="relative">
	    <input
	    type="url"
	    value={focused ? formData.redirect_url : formatRedirectUrl(formData.redirect_url)}
	    onFocus={() => setFocused(true)}
	    onBlur={() => setFocused(false)}
	    onChange={(e) => handleInputChange('redirect_url', e.target.value)}
	    className={`
      w-full px-3 py-2 pr-10 border rounded-lg 
      focus:outline-none focus:ring-2 focus:ring-primary-500
      transition-colors
      ${isDark
		      ? 'bg-dark-700 border-dark-500 text-white placeholder-gray-400'
		      : 'bg-white border-gray-400 text-gray-900 placeholder-gray-500'}
      hover:border-primary-400 focus:border-primary-500
    `}
	    placeholder="Cliquez ou tapez pour modifier l'URL"
	    />
	    <PencilIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
	    </div>
	    {isRedirectUrlVCard && (
		    <div className="mt-4">
		    <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${
			    isDark ? 'text-gray-300' : 'text-gray-700'
		    }`}>
		    {content.vcardTitle} #{ vcardNumber }
		    </p>
		    <div className={`grid grid-cols-2 gap-3 text-sm p-3 rounded-lg border ${
			    isDark
				    ? 'bg-dark-800 text-gray-100 border-dark-700'
				    : 'bg-gray-100 text-gray-800 border-gray-300'
		    }`}>
		    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.name}</div>
		    <div className="truncate">{name}</div>

		    {company && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.company}</div>
			    <div className="truncate">{company}</div>
			    </>
		    )}

		    {phone && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.phone}</div>
			    <div className="truncate">{phone}</div>
			    </>
		    )}

		    {mail && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.mail}</div>
			    <div className="truncate">{mail}</div>
			    </>
		    )}
		    </div>
		    {/* edit button */}
		    <Link
		    to="/dashboard/vcard"
		    className={ isDark ?
			    `mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors
		    border-primary-500 text-primary-500 hover:bg-gray-700` :
			    `mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 border text-sm font-medium transition-colors
		    text-gray-100 hover:bg-primary-800`
		    }
		    >
		    {content.manageVCard}
		    </Link>
		    </div>
	    )}
	    {vcardFetchError && (
		    <div className="mt-4">
		    <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${
			    isDark ? 'text-gray-300' : 'text-gray-700'
		    }`}>
		    VCard
		    </p>
		    <div className={`grid grid-cols-2 gap-3 text-sm p-3 rounded-lg border ${
			    isDark
				    ? 'bg-dark-800 text-gray-100 border-dark-700'
				    : 'bg-gray-100 text-gray-800 border-gray-300'
		    }`}>
		    <div className="col-span-2 font-medium truncate text-red-500 dark:text-red-400">
		    {content.vcardDeleted}
		    </div>
		    </div>
		    </div>
	    )}
	    <VCardPicker
	    onChange={(url) => setFormData((prev) => ({ ...prev, redirect_url: url }))}
	    />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
	  {content.maxRedirLabel}
          </label>
          <input
            type="number"
            value={formData.max_redirect || ''}
            onChange={(e) => handleInputChange('max_redirect', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
            placeholder={content.unlimited}
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
		{content.expireLabel}
          </label>
            <input type="datetime-local"
                  value={formData.validity_time_min || ''}
                  onChange={(e) => handleInputChange('validity_time_min', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
            />
        </div>
      </div>

        <div className={`p-3 border rounded-lg ${
        isDark 
          ? 'bg-blue-900/20 border-blue-500/30' 
          : 'bg-blue-50 border-blue-200'
      }`}>
        <p className={`text-sm ${
          isDark ? 'text-blue-300' : 'text-blue-600'
        }`}>
          <strong>{content.designTipBold}</strong>{content.designTip}
        </p>
      </div>
    </div>
  );

  const resolveAIField = (field) => {
    return (
      qrCode?.[field] ||
      qrCode?.metadata?.[field] ||
      qrCode?.extra_data?.[field] ||
      qrCode?.details?.[field] ||
      qrCode?.data?.[field]
    );
  };

  const renderAIQRFields = () => {
    const positivePrompt =
      resolveAIField('generation_prompt') ||
      resolveAIField('prompt') ||
      resolveAIField('positive_prompt');
    const negativePrompt =
      resolveAIField('negative_prompt') ||
      resolveAIField('prompt_negative');

    return (
      <div className="space-y-4">
        {(positivePrompt || negativePrompt) && (
          <div className={`p-4 rounded-xl border space-y-3 ${
            isDark
              ? 'bg-dark-700/70 border-dark-600'
              : 'bg-white border-gray-200 shadow-sm'
          }`}>
            <div>
              <p className={`text-sm font-semibold mb-1 ${
                isDark ? 'text-gray-200' : 'text-gray-800'
              }`}>
		{content.promptsTitle}
              </p>
              <p className={`text-xs ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
		{content.promptsRO}
              </p>
            </div>
            {positivePrompt && (
              <div>
                <p className={`text-xs uppercase tracking-wide mb-1 ${
                  isDark ? 'text-primary-300' : 'text-primary-600'
                }`}>
		{content.mainPrompt}
                </p>
                <pre className={`text-sm whitespace-pre-wrap break-words p-3 rounded-lg ${
                  isDark ? 'bg-dark-800 text-gray-100' : 'bg-gray-100 text-gray-800'
                }`}>
                  {positivePrompt}
                </pre>
              </div>
            )}
            {negativePrompt && (
              <div>
                <p className={`text-xs uppercase tracking-wide mb-1 ${
                  isDark ? 'text-secondary-300' : 'text-secondary-600'
                }`}>
		    {content.negPrompt}
                </p>
                <pre className={`text-sm whitespace-pre-wrap break-words p-3 rounded-lg ${
                  isDark ? 'bg-dark-800 text-gray-100' : 'bg-gray-100 text-gray-800'
                }`}>
                  {negativePrompt}
                </pre>
              </div>
            )}
          </div>
        )}
        <div>
          <label className={`block text-sm font-medium mb-2 ${
            isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>
	    {content.redirectLabel}
          </label>
	    <div className="relative">
	    <input
	    type="url"
	    value={focused ? formData.redirect_url : formatRedirectUrl(formData.redirect_url)}
	    onFocus={() => setFocused(true)}
	    onBlur={() => setFocused(false)}
	    onChange={(e) => handleInputChange('redirect_url', e.target.value)}
	    className={`
      w-full px-3 py-2 pr-10 border rounded-lg 
      focus:outline-none focus:ring-2 focus:ring-primary-500
      transition-colors
      ${isDark
		      ? 'bg-dark-700 border-dark-500 text-white placeholder-gray-400'
		      : 'bg-white border-gray-400 text-gray-900 placeholder-gray-500'}
      hover:border-primary-400 focus:border-primary-500
    `}
	    placeholder="Cliquez ou tapez pour modifier l'URL"
	    />
	    <PencilIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
	    </div>
	    {isRedirectUrlVCard && (
		    <div className="mt-4">
		    <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${
			    isDark ? 'text-gray-300' : 'text-gray-700'
		    }`}>
		    {content.vcardTitle} #{ vcardNumber } {content.vcardPreviewHint}
		    </p>
		    <div className={`grid grid-cols-2 gap-3 text-sm p-3 rounded-lg border ${
			    isDark
				    ? 'bg-dark-800 text-gray-100 border-dark-700'
				    : 'bg-gray-100 text-gray-800 border-gray-300'
		    }`}>
		    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.name}</div>
		    <div className="truncate">{name}</div>

		    {company && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.company}</div>
			    <div className="truncate">{company}</div>
			    </>
		    )}

		    {phone && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.phone}</div>
			    <div className="truncate">{phone}</div>
			    </>
		    )}

		    {mail && (
			    <>
			    <div className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{content.mail}</div>
			    <div className="truncate">{mail}</div>
			    </>
		    )}
		    </div>
		    {/* edit button */}
		    <Link
		    to="/dashboard/vcard"
		    className={ isDark ?
			    `mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors
		    border-primary-500 text-primary-500 hover:bg-gray-700` :
			    `mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 border text-sm font-medium transition-colors
		    text-gray-100 hover:bg-primary-800`
		    }
		    >
		    {content.manageVCard}
		    </Link>
		    </div>
	    )}
	    {vcardFetchError && (
		    <div className="mt-4">
		    <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${
			    isDark ? 'text-gray-300' : 'text-gray-700'
		    }`}>
		    VCard
		    </p>
		    <div className={`grid grid-cols-2 gap-3 text-sm p-3 rounded-lg border ${
			    isDark
				    ? 'bg-dark-800 text-gray-100 border-dark-700'
				    : 'bg-gray-100 text-gray-800 border-gray-300'
		    }`}>
		    <div className="col-span-2 font-medium truncate text-red-500 dark:text-red-400">
		    {content.vcardDeleted}
		    </div>
		    </div>
		    </div>
	    )}
	    <VCardPicker
	    onChange={(url) => setFormData((prev) => ({ ...prev, redirect_url: url }))}
	    />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
	  {content.maxRedirLabel}
            </label>
            <input
              type="number"
              value={formData.max_redirect || ''}
              onChange={(e) => handleInputChange('max_redirect', e.target.value)}
              min="1"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                isDark 
                  ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
	    placeholder={content.unlimited}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
		{content.expireLabel}
            </label>
            <input type="datetime-local"
                  value={formData.validity_time_min || ''}
                  onChange={(e) => handleInputChange('validity_time_min', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGS1Fields = () => (
    <div className="space-y-4">
      <div className={`p-3 border rounded-lg ${
        isDark 
          ? 'bg-amber-900/20 border-amber-500/30' 
          : 'bg-amber-50 border-amber-200'
      }`}>
        <p className={`text-sm ${
          isDark ? 'text-amber-300' : 'text-amber-600'
        }`}>
          <strong>Modification GS1 :</strong> Modifiez les paramètres ci-dessous pour régénérer automatiquement le lien GS1 de redirection.
        </p>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
	  {content.baseUrl}
        </label>
        <input
          type="url"
          value={formData.base_url || ''}
          onChange={(e) => handleInputChange('base_url', e.target.value)}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
          placeholder="https://id.gs1.org"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
            GTIN
          </label>
          <input
            type="text"
            value={formData.gtin || ''}
            onChange={(e) => handleInputChange('gtin', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
            placeholder="1234567890123"
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
            Numéro de lot
          </label>
          <input
            type="text"
            value={formData.lot || ''}
            onChange={(e) => handleInputChange('lot', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
            placeholder="LOT123"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
	  {content.exp}
          </label>
          <input
            type="text"
            value={formData.exp || ''}
            onChange={(e) => handleInputChange('exp', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
            placeholder="251231"
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
            Langue
          </label>
          <select
            value={formData.lang || 'fr'}
            onChange={(e) => handleInputChange('lang', e.target.value)}
            className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="fr">Français</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="de">Deutsch</option>
            <option value="it">Italiano</option>
          </select>
        </div>
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
	  {content.linkType}
        </label>
        <select
          value={formData.link_type || 'pip'}
          onChange={(e) => handleInputChange('link_type', e.target.value)}
          className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="pip">Informations produit</option>
          <option value="instruction">Instructions</option>
          <option value="recall">Rappel produit</option>
          <option value="traceability">Traçabilité</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
	  {content.maxRedirLabel}
          </label>
          <input
            type="number"
            value={formData.max_redirect || ''}
            onChange={(e) => handleInputChange('max_redirect', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isDark 
              ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
            placeholder={content.unlimited}
          />
        </div>
        <div>
          <label className={`block text-sm font-medium mb-2 ${
          isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
		{content.expireLabel}
          </label>
            <input type="datetime-local"
                  value={formData.validity_time_min || ''}
                  onChange={(e) => handleInputChange('validity_time_min', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${isDark ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'}`}
            />
        </div>
      </div>
    </div>
  );

  const renderFields = () => {
    switch (qrCode?.item_type) {
      case 'url':
      case 'classic':
        return renderClassicQRFields();
      case 'ai':
        return renderAIQRFields();
      case 'gs1':
        return renderGS1Fields();
      default:
        return renderClassicQRFields();
    }
  };


if (!content) {
  return (
    <div className={`border-t ${isDark ? 'border-dark-700 bg-dark-750' : 'border-gray-200 bg-gray-50'}`}>
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 space-y-2">
              <div className={`h-6 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
              <div className={`h-4 w-32 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
            </div>
            <div className={`ml-6 w-40 h-40 rounded-lg ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
          </div>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className={`h-4 w-40 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
              <div className={`h-10 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
            </div>
          ))}
          <div className="flex justify-end space-x-3 pt-6">
            <div className={`h-10 w-20 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
            <div className={`h-10 w-24 rounded ${isDark ? 'bg-dark-700' : 'bg-gray-300'}`} />
          </div>
        </div>
      </div>
    </div>
  );
}

  return (
    <div className={`border-t ${
      isDark 
        ? 'border-dark-700 bg-dark-750' 
        : 'border-gray-200 bg-gray-50'
    }`}>
      <div className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
	  {content.title}
            </h3>
            <p className={`text-sm mt-1 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
	  {content.subtitle} {qrCode?.item_type?.toUpperCase() || 'CLASSIC'}
            </p>
          </div>
          
          <div className="ml-6 flex-shrink-0">
            <div className={`w-40 h-40 rounded-lg overflow-hidden border relative ${
              isDark 
                ? 'bg-dark-700 border-dark-600' 
                : 'bg-white border-gray-300'
            }`}>
              {qrImage ? (
                <>
                  <img 
                    src={qrImage} 
                    alt={content.qrAlt} 
                    className="w-full h-full object-contain"
                  />
                  {isLoadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className={`text-sm ${
                    isDark ? 'text-gray-500' : 'text-gray-400'
                  }`}>QR Code</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className={`mb-4 p-3 border rounded-lg text-sm ${
            isDark 
              ? 'bg-red-900/20 border-red-500 text-red-400' 
              : 'bg-red-50 border-red-200 text-red-600'
          }`}>
            {error}
          </div>
        )}

        {success && (
          <div className={`mb-4 p-3 border rounded-lg text-sm ${
            isDark 
              ? 'bg-green-900/20 border-green-500 text-green-400' 
              : 'bg-green-50 border-green-200 text-green-600'
          }`}>
            {success}
          </div>
        )}

        {renderFields()}

        <div className={`flex items-center justify-end space-x-3 mt-6 pt-6 border-t ${
          isDark ? 'border-dark-700' : 'border-gray-200'
        }`}>
          <button
            onClick={onCancel}
            disabled={isLoading}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
              isDark 
                ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
            }`}
          >
	  {content.cancelBtn}
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <CheckIcon className="w-4 h-4 mr-2" />
            )}
	  {content.saveBtn}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeEditorInline;
